import { Button, CircularProgress, Stack, Typography } from "@mui/material";
import { ErrorText } from "Components/Shared/Errors/ErrorText";
import { PrimaryPaper } from "Components/Shared/PrimaryPaper";
import { useTriggerQuickPayment } from "Hooks/useTriggerQuickPayment";
import type { BankEnvironment } from "Infrastructure/Api/Api";
import { Navigate } from "Navigation/Navigate";
import { useNavigate } from "Navigation/useNavigate";
import type { ReactNode } from "react";
import { Resources, useResource } from "Translations/Resources";
import { appUrl } from "Utils/UrlUtils";

const RESOURCES = Resources.Customer.Pay.QuickPayment;

type Props = {
  environment: BankEnvironment;
  transactionPublicID: string;
};

export const QuickPaymentComponent = ({
  environment,
  transactionPublicID,
}: Props) => {
  const { t } = useResource();
  const navigate = useNavigate();

  // Auto-triggers payment
  const { error, isQuickPaymentUnavailable } = useTriggerQuickPayment(
    transactionPublicID,
    environment,
  );

  // The quick payment "attempted" flag is already in local storage, so re-entering the same route
  // renders the regular payment form.
  const paymentUrl = `${appUrl("customer-payment-pay")}/${transactionPublicID}`;
  const navigateToPayment = () => navigate(paymentUrl);

  if (isQuickPaymentUnavailable) {
    return <Navigate to={paymentUrl} replace />;
  }

  if (error) {
    return (
      <Layout>
        <Stack
          height="100%"
          width="100%"
          justifyContent="space-between"
          alignItems="center"
          gap={4}
          pb={2}
        >
          <Stack
            alignItems="center"
            justifyContent="center"
            textAlign="center"
            gap={3}
            flex={1}
          >
            <Typography variant="h5" fontWeight={600}>
              {t(RESOURCES.Error)}
            </Typography>
            <ErrorText>{error}</ErrorText>
          </Stack>
          <Button
            fullWidth
            color="primary"
            variant="contained"
            onClick={navigateToPayment}
          >
            {t(RESOURCES.UseAnotherAccount)}
          </Button>
        </Stack>
      </Layout>
    );
  }

  return (
    <Layout>
      <CircularProgress />
      <Typography fontWeight={700}>{t(RESOURCES.Processing)}</Typography>
    </Layout>
  );
};

const Layout = ({ children }: { children: ReactNode }) => (
  <PrimaryPaper sx={{ height: ["100%", "auto"] }}>
    <Stack gap={4} alignItems="center" justifyContent="center" height="100%">
      {children}
    </Stack>
  </PrimaryPaper>
);
